import { useState, useEffect, type FC } from "react";
import {
  getWorkerIdForUser,
  updateWorkerLocation,
} from "../../services/workers";

interface LocationActionsProps {
  currentLocation: { latitude: number; longitude: number } | null;
  setCurrentLocation: (
    _location: { latitude: number; longitude: number } | null
  ) => void;
  setError: (_error: string | null) => void;
  userId: string | undefined;
}

const LocationActions: FC<LocationActionsProps> = ({
  currentLocation,
  setCurrentLocation,
  setError,
  userId,
}) => {
  // Add state for tracking location request status
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [geoLocationError, setGeoLocationError] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isIPadOS: false,
    isAndroid: false,
    isMobile: false,
    browserName: "",
    osVersion: "",
    browserVersion: "",
  });

  // Detect device on component mount
  useEffect(() => {
    const ua = navigator.userAgent;

    // Detect iOS and iPadOS
    const isIPad =
      ua.includes("iPad") ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const isIPhone = /iPhone|iPod/.test(ua);
    const isIOS = isIPhone;
    const isIPadOS = isIPad;

    // Detect Android
    const isAndroid = ua.includes("Android");
    const isMobile = isIOS || isIPadOS || isAndroid || /Mobi|Android/i.test(ua);

    // Extract OS version
    let osVersion = "";
    if (isIOS || isIPadOS) {
      const match = ua.match(/(iPhone|iPad|iPod).*OS\s([\d_]+)/);
      if (match && match[2]) {
        osVersion = match[2].replace(/_/g, ".");
      }
    } else if (isAndroid) {
      const match = ua.match(/Android\s([\d.]+)/);
      if (match && match[1]) {
        osVersion = match[1];
      }
    }

    // Detect browser
    let browserName = "Unknown";
    let browserVersion = "";

    if (ua.includes("CriOS")) {
      browserName = "Chrome";
      const match = ua.match(/CriOS\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("FxiOS")) {
      browserName = "Firefox";
      const match = ua.match(/FxiOS\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("EdgiOS")) {
      browserName = "Edge";
      const match = ua.match(/EdgiOS\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("Chrome")) {
      browserName = "Chrome";
      const match = ua.match(/Chrome\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
      browserName = "Safari";
      const match = ua.match(/Version\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("Firefox")) {
      browserName = "Firefox";
      const match = ua.match(/Firefox\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    } else if (ua.includes("Edge")) {
      browserName = "Edge";
      const match = ua.match(/Edge\/([\d.]+)/);
      if (match && match[1]) browserVersion = match[1];
    }

    setDeviceInfo({
      isIOS,
      isIPadOS,
      isAndroid,
      isMobile,
      browserName,
      osVersion,
      browserVersion,
    });

    console.log("Device detected:", {
      isIOS,
      isIPadOS,
      isAndroid,
      browserName,
      osVersion,
      browserVersion,
    });
  }, []);

  // Get geolocation with device-specific handling
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolokalisierung wird von Ihrem Browser nicht unterstützt");
      setGeoLocationError(true);
      return;
    }

    setIsRequestingLocation(true);
    setGeoLocationError(false);
    setError(null);

    // Configure options based on device
    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Default timeout
      maximumAge: 0,
    };

    // Adjust settings based on device
    if (deviceInfo.isIOS) {
      // iOS devices may need longer timeouts
      options.timeout = 20000;
      console.log("iOS device detected, using optimized settings");
    } else if (deviceInfo.isIPadOS) {
      // iPadOS devices may need specific settings
      options.timeout = 20000;
      console.log("iPadOS device detected, using optimized settings");
    } else if (deviceInfo.isAndroid) {
      // Android devices often work better with slightly different settings
      options.timeout = 15000;
      console.log("Android device detected, using optimized settings");
    }

    // Safari-specific adjustments
    if (deviceInfo.browserName === "Safari") {
      // Safari sometimes needs special handling
      options.enableHighAccuracy = false; // Can be more reliable in Safari
      console.log("Safari browser detected, using optimized settings");
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log("Got current location:", position);
        try {
          if (userId) {
            const workerId = await getWorkerIdForUser(userId);
            await updateWorkerLocation(
              workerId,
              position.coords.latitude.toString(),
              position.coords.longitude.toString()
            );
          }
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setError(null);
          setIsRequestingLocation(false);
          setGeoLocationError(false);
        } catch (error) {
          console.error("Error updating worker location:", error);
          setError("Fehler beim Aktualisieren des Arbeiterstandorts");
          setIsRequestingLocation(false);
        }
      },
      (error) => {
        setIsRequestingLocation(false);
        setGeoLocationError(true);
        console.error("Geolocation error:", error.code, error.message);

        // Provide detailed error messages based on error code and device
        switch (error.code) {
          case error.PERMISSION_DENIED:
            if (deviceInfo.isIPadOS) {
              setError(
                `Standortzugriff verweigert auf iPadOS. Bitte gehen Sie zu Einstellungen > Safari > Websites > Standort und erlauben Sie den Zugriff für diese Website.${
                  deviceInfo.browserName !== "Safari"
                    ? " Wenn Sie einen anderen Browser verwenden, prüfen Sie dessen Einstellungen."
                    : ""
                }`
              );
            } else if (deviceInfo.isIOS) {
              setError(
                `Standortzugriff verweigert auf iOS. Bitte gehen Sie zu Einstellungen > Safari > Websites > Standort und erlauben Sie den Zugriff für diese Website.${
                  deviceInfo.browserName !== "Safari"
                    ? " Wenn Sie einen anderen Browser verwenden, prüfen Sie dessen Einstellungen."
                    : ""
                }`
              );
            } else if (deviceInfo.isAndroid) {
              setError(
                `Standortzugriff verweigert auf Android. Bitte gehen Sie zu Einstellungen > Apps > ${deviceInfo.browserName} > Berechtigungen > Standort und erlauben Sie den Zugriff.`
              );
            } else {
              setError(
                "Standortzugriff verweigert. Bitte aktivieren Sie den Standortzugriff in Ihren Browsereinstellungen."
              );
            }
            break;
          case error.POSITION_UNAVAILABLE:
            setError(
              "Standortinformationen sind auf diesem Gerät nicht verfügbar. Bitte prüfen Sie, ob die Standortdienste aktiviert sind."
            );
            break;
          case error.TIMEOUT:
            setError(
              "Zeitüberschreitung bei der Standortanfrage. Bitte versuchen Sie es erneut oder prüfen Sie Ihre Internetverbindung."
            );
            break;
          default:
            setError(
              "Standort konnte nicht ermittelt werden. Bitte versuchen Sie es später erneut."
            );
        }
      },
      options
    );
  };

  // Function to retry location request with different settings
  const retryWithDifferentSettings = () => {
    setError("Versuche mit alternativen Einstellungen...");
    setGeoLocationError(false);

    // Use different settings for the retry
    const retryOptions = {
      enableHighAccuracy: !deviceInfo.isIOS, // Toggle this setting
      timeout: 30000, // Longer timeout for retry
      maximumAge: 60000, // Allow cached positions for retry
    };

    setIsRequestingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log("Got location on retry:", position);
        try {
          if (userId) {
            const workerId = await getWorkerIdForUser(userId);
            await updateWorkerLocation(
              workerId,
              position.coords.latitude.toString(),
              position.coords.longitude.toString()
            );
          }
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setError(null);
          setIsRequestingLocation(false);
        } catch (error) {
          console.error("Error updating worker location on retry:", error);
          setError("Fehler beim Aktualisieren des Arbeiterstandorts");
          setIsRequestingLocation(false);
        }
      },
      (error) => {
        console.error("Geolocation retry error:", error.code, error.message);
        setIsRequestingLocation(false);
        setGeoLocationError(true);
        setError(
          "Standortermittlung fehlgeschlagen. Bitte prüfen Sie Ihre Geräteeinstellungen und versuchen Sie es erneut."
        );
      },
      retryOptions
    );
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-gray-800">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center gap-6">
          <button
            onClick={getCurrentLocation}
            disabled={isRequestingLocation}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
              isRequestingLocation
                ? "cursor-not-allowed bg-gray-500"
                : "bg-primary-700 hover:bg-primary-800"
            }`}
          >
            {isRequestingLocation ? "Wird geladen..." : "Aktueller Standort"}
          </button>
          {currentLocation && (
            <div className="flex gap-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">Längengrad:</span>{" "}
                {currentLocation.longitude.toFixed(6)}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <span className="font-medium">Breitengrad:</span>{" "}
                {currentLocation.latitude.toFixed(6)}
              </p>
            </div>
          )}
        </div>

        {/* Retry button - only show after a geolocation error */}
        {!isRequestingLocation && geoLocationError && (
          <div>
            <button
              onClick={retryWithDifferentSettings}
              className="text-sm text-primary-700 hover:text-primary-800 hover:underline"
            >
              Mit alternativen Einstellungen versuchen
            </button>
          </div>
        )}

        {/* Device info - helpful for debugging */}
        {deviceInfo.isMobile && (
          <p className="mt-2 text-xs text-gray-500">
            Gerät:{" "}
            {deviceInfo.isIPadOS
              ? "iPadOS"
              : deviceInfo.isIOS
              ? "iOS"
              : deviceInfo.isAndroid
              ? "Android"
              : "Mobil"}{" "}
            {deviceInfo.osVersion}, Browser: {deviceInfo.browserName}{" "}
            {deviceInfo.browserVersion}
          </p>
        )}
      </div>
    </div>
  );
};

export default LocationActions;
